import React from 'react';
import PropTypes from 'prop-types';

import backend from 'utils/backend';
import {dispatch, connect} from 'utils/state_manager';

import {Typography, Box, Link} from 'views/components';
import {Translate} from 'views/module';

import moment from 'moment';

const NavbarService = (props) => {
  const {
    user,
    statusProgressWrap: progressWrap,
    taskRefresh,
    title,
    options = [],
    currentPath,
  } = props;

  const [myTaskCount, setMyTaskCount] = React.useState(0);
  const [allTaskCount, setAllTaskCount] = React.useState(0);
  const [leadsCount, setLeadsCount] = React.useState(0);

  const processTaskList = React.useCallback((taskList) => {
    return taskList
        .filter((task) => moment(task.dueDate, moment.ISO_8601, true).isValid())
        .sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf());
  }, []);

  const isButtonSelected = (buttonLink) => {
    if (buttonLink === '/') {
      return currentPath === '/';
    }

    return currentPath === buttonLink || currentPath.startsWith(`${buttonLink}/`);
  };

  React.useEffect(() => {
    if (!user?.userRn) {
      setMyTaskCount(0);
      return;
    }

    progressWrap(async () => {
      try {
        const userTaskResponse = await backend.workflowEngine.getUserTasks(user.userRn);
        const userTasks = userTaskResponse?.list || [];
        const relevantIncompleteTasks = userTasks
            .filter((task) => task?.status !== 'COMPLETE')
            .filter((task) =>
              moment(task.dueDate, moment.ISO_8601, true).isValid() &&
              moment(task.dueDate).isBetween(
                  moment().startOf('day').utc(),
                  moment().endOf('day').utc(),
                  null,
                  '[]',
              ),
            )
            .sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf());

        setMyTaskCount(relevantIncompleteTasks.length);
      } finally {
      }
    });
  }, [progressWrap, taskRefresh, user?.userRn]);

  React.useEffect(() => {
    if (!user?.userRn) {
      setAllTaskCount(0);
      return;
    }

    progressWrap(async () => {
      try {
        const startOfDayUtc = moment().startOf('day').utc().toISOString();
        const endOfWeekUtc = moment().endOf('isoWeek').utc().toISOString();
        const allTaskResponse = await backend.workflowEngine.getAllTasks(startOfDayUtc, endOfWeekUtc);
        const allTasks = allTaskResponse?.list || [];
        const processedTaskList = processTaskList(allTasks);

        setAllTaskCount(processedTaskList.length);
      } finally {
      }
    });
  }, [processTaskList, progressWrap, taskRefresh]);

  React.useEffect(() => {
    progressWrap(async () => {
      try {
        const filters = [
          {key: 'status', value: 'LEAD'},
        ];
        const result = await backend.client.queryClient(filters);
        setLeadsCount((result?.list ?? []).length);
      } finally {
      }
    });
  }, [progressWrap, taskRefresh]);

  return (
    <Box
      sx={{
        py: 1,
        px: 2,
        borderRadius: '8px',
      }}
    >
      {
        Boolean(title) &&
          <Typography
            sx={{
              'display': 'block',
              'py': '6px',
              'filter': 'contrast(25%)',
              'fontWeight': 400,
              'fontSize': '12px',
              'color': 'navbar.text',
            }}
          >
            <Translate expression={title} />
          </Typography>
      }
      {
        options.map((button, index) => {
          return (
            <Link
              href={button.link}
              sx={{
                'display': 'block',
                'borderRadius': '6px',
                'backgroundColor': isButtonSelected(button.link) ? 'background.highlighted' : undefined,
                'textDecoration': 'none',
              }}
              key={index}
            >
              <Box
                sx={{
                  'display': 'flex',
                  'alignItems': 'center',
                  'justifyContent': 'space-between',
                  'color': 'navbar.text',
                  'py': '6px',
                  'px': '16px',
                  'fontSize': '14px',
                  '&:hover': {
                    'backgroundColor': 'background.highlighted',
                    'borderRadius': '6px',
                  },
                }}
              >
                <Box
                  sx={{
                    'display': 'flex',
                    'alignItems': 'center',
                    'gap': '12px',
                  }}
                >
                  {button.icon}
                  <Typography
                    sx={{
                      color: 'navbar.text',
                      fontSize: '14px',
                      fontWeight: isButtonSelected(button.link) ? 500 : 400,
                    }}
                  >
                    <Translate expression={button.title} />
                  </Typography>
                </Box>
                {
                  (
                    (button.count === 'mytasks' && myTaskCount > 0) ||
                    (button.count === 'alltasks' && allTaskCount > 0) ||
                    (button.count === 'leads' && leadsCount > 0)
                  ) &&
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isButtonSelected(button.link) ? '#FEF2F2' : 'background.highlighted',
                        fontSize: '12px',
                        width: '1.75rem',
                        borderRadius: 1,
                        border: isButtonSelected(button.link) ? '2px solid #FECACA' : '2px solid #e8e4de',
                        color: isButtonSelected(button.link) ? '#DC2626' :'#a8a49e',
                      }}
                    >
                      {
                        button.count === 'mytasks' && myTaskCount
                      }
                      {
                        button.count === 'alltasks' && allTaskCount
                      }
                      {
                        button.count === 'leads' && leadsCount
                      }
                    </Box>
                }
              </Box>
            </Link>
          );
        })
      }
    </Box>
  );
};
NavbarService.propTypes = {
  title: PropTypes.string,
  currentPath: PropTypes.string,
  options: PropTypes.array,
  user: PropTypes.object,
  taskRefresh: PropTypes.number,
  statusProgressWrap: PropTypes.func.isRequired,
};
const mapStateToProps = (state) => ({
  taskRefresh: state.taskRefresh?.value,
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(NavbarService));
